import React, { useContext } from 'react';
import { compose } from 'redux';
import { withTranslation, useTranslation } from 'react-i18next';
import { ALL_FOLDER_ID } from '@wix/da-url';
import shortenNumber from '@wix/da-shared-react/pkg/utils/shortenNumber';
import { IconSize } from '@wix/da-shared-react/pkg/Icons/IconWrap';
import PremiumFolderDataIcon from '@wix/da-shared-react/pkg/GallectionFolderDropdown/_partials/PremiumFolderDataIcon';
import SortOrderSwitcher from '../SortOrderSwitcher';
import FolderBarFolderDropdown from './_partials/FolderBarFolderDropdown';
import { getStickyContentHeight } from '../../helpers';
import {
  getGallectionsStickyBarHeight,
  getCommentsBlockAnchor,
} from '../helpers';
import { PapiGallection } from '@wix/da-types-papi';
import {
  withPerspectiveContext,
  PerspectiveContextProps,
  withMobileContext,
  MobileContext,
} from '@wix/da-shared-react/pkg/Context';
import EditGallectionButton from '../EditGallectionButton';

import s from './FolderBar.scss';

export interface Props {
  currentFolder?: PapiGallection | null;
  unsetSelectedFolderId: () => void;
  pauseCurrentFolderDeviationFetching: () => void;
  withBackButton?: boolean;
}

const enhance = compose(
  withPerspectiveContext,
  withMobileContext,
  withTranslation()
);

export const FolderBar: React.FC<Props & PerspectiveContextProps> = ({
  currentFolder,
  withBackButton,
  getScrollableContainer,
  pauseCurrentFolderDeviationFetching,
  unsetSelectedFolderId,
}) => {
  const isMobile = useContext(MobileContext);
  const { t } = useTranslation();
  if (!currentFolder) {
    return null;
  }

  const vespaSearchEngineIsReady = false;

  const onCommentLinkClick = e => {
    e.preventDefault();

    pauseCurrentFolderDeviationFetching();
    scrollToCommentBlock();
  };

  const scrollToCommentBlock = () => {
    const commentBlockNode = document.getElementById(
      getCommentsBlockAnchor(currentFolder.folderId)
    );
    if (!commentBlockNode) {
      return;
    }

    const offsetTop =
      getStickyContentHeight(isMobile) +
      getGallectionsStickyBarHeight(isMobile);

    const scrollableContainer = getScrollableContainer();

    scrollableContainer &&
      scrollableContainer.scrollTo(
        0,
        scrollableContainer.scrollTop +
          commentBlockNode.getBoundingClientRect().top -
          offsetTop
      );
  };

  return (
    <>
      <div className={s['folder-info']}>
        {withBackButton && (
          <div className={s['back']} onClick={unsetSelectedFolderId}>
            {t!('pages.gallections.back')}
          </div>
        )}
        <PremiumFolderDataIcon
          premiumFolderData={currentFolder?.premiumFolderData}
          size={IconSize.COMPACT}
          className={s['premium-folder-icon']}
        />
        <FolderBarFolderDropdown currentFolder={currentFolder} />
        <a
          className={s['comments-count']}
          href={`#${getCommentsBlockAnchor(currentFolder.folderId)}`}
          onClick={onCommentLinkClick}
        >
          {t!('common.comments_plural')}
          <span className={s['count']}>
            {shortenNumber(currentFolder.commentCount)}
          </span>
        </a>
      </div>
      <div className={s['right-zone']}>
        {!isMobile && currentFolder!.folderId !== ALL_FOLDER_ID && (
          <EditGallectionButton gallection={currentFolder} />
        )}
        {vespaSearchEngineIsReady && (
          <SortOrderSwitcher className={s['sort-order-switch']} />
        )}
      </div>
    </>
  );
};
FolderBar.displayName = 'FolderBar';

export default enhance(FolderBar) as React.ComponentType<Props>;
